/**
 * Tauschbörse.
 *
 * Zwei Listen: was andere abgeben, und was ich selbst angeboten habe. Für die
 * Planung kommt eine dritte Möglichkeit dazu — freigeben oder ablehnen.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'

import {
  ApiError,
  SEVERITY_TONE,
  api,
  type ListResponse,
  type Me,
  type PlanConflict,
  type ShiftApplication,
} from '../lib/api'
import { listItemVariants, listVariants, springSoft } from '../lib/motion'
import { Banner, Button, Card, Chip, Spinner, useToast } from '../ui'
import './me.css'

type Swap = {
  id: string
  assignment_id: string
  shift_id: string
  starts_at: string
  ends_at: string
  status: string
  offered_by: string
  claimed_by: string | null
}

function when(swap: Swap): string {
  const start = new Date(swap.starts_at)
  const end = new Date(swap.ends_at)
  return (
    start.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) +
    ', ' +
    start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) +
    '\u2013' +
    end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  )
}

export function SwapsPage({ me }: { me: Me }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const canDecide = me.active_tenant?.role === 'owner' || me.active_tenant?.role === 'manager'

  const others = useQuery({
    queryKey: ['swaps', 'others'],
    queryFn: () => api.get<ListResponse<Swap>>('/api/swaps'),
  })
  const mine = useQuery({
    queryKey: ['swaps', 'mine'],
    queryFn: () => api.get<ListResponse<Swap>>('/api/swaps?mine=true'),
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['swaps'] })
    queryClient.invalidateQueries({ queryKey: ['my-shifts'] })
  }

  const claim = useMutation({
    mutationFn: (id: string) =>
      api.post<{ auto_approved: boolean; conflicts: PlanConflict[] }>(`/api/swaps/${id}/claim`),
    onSuccess: (result) => {
      refresh()
      toast(
        result.auto_approved
          ? 'Übernommen — die Schicht steht jetzt bei dir.'
          : 'Angefragt. Die Schichtleitung entscheidet.',
        'success',
      )
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  const decide = useMutation({
    mutationFn: (input: { id: string; approve: boolean }) =>
      api.post(`/api/swaps/${input.id}/decide`, { approve: input.approve }),
    onSuccess: () => {
      refresh()
      toast('Entschieden.', 'success')
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  const withdraw = useMutation({
    mutationFn: (id: string) => api.delete(`/api/swaps/${id}`),
    onSuccess: () => {
      refresh()
      toast('Angebot zurückgezogen.', 'success')
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  if (others.isPending || mine.isPending) return <Spinner />

  return (
    <div className="stack">
      <h1>Tauschbörse</h1>

      {/* Bewerbungen stehen oben: Sie warten auf eine Entscheidung, die
          Tauschbörse darunter ist Selbstbedienung. */}
      {canDecide && <PendingApplications />}

      <Card title="Abzugeben">
        {others.data?.items.length === 0 ? (
          <p className="muted">Gerade bietet niemand eine Schicht an.</p>
        ) : (
          <motion.div
            className="stack stack--tight"
            variants={listVariants(others.data?.items.length ?? 0)}
            initial="hidden"
            animate="visible"
          >
            {others.data?.items.map((swap) => (
              <motion.div
                key={swap.id}
                className="swap"
                variants={listItemVariants}
                transition={springSoft()}
              >
                <span>
                  <span className="swap__when">{when(swap)}</span>
                  <br />
                  <span className="muted">von {swap.offered_by}</span>
                </span>
                <span className="row">
                  {swap.status === 'claimed' ? (
                    <Chip tone="warning">
                      {swap.claimed_by ? `${swap.claimed_by} möchte übernehmen` : 'angefragt'}
                    </Chip>
                  ) : (
                    <Button small busy={claim.isPending} onClick={() => claim.mutate(swap.id)}>
                      Übernehmen
                    </Button>
                  )}
                  {canDecide && swap.status === 'claimed' && (
                    <>
                      <Button
                        small
                        busy={decide.isPending}
                        onClick={() => decide.mutate({ id: swap.id, approve: true })}
                      >
                        Freigeben
                      </Button>
                      <Button
                        variant="text"
                        small
                        onClick={() => decide.mutate({ id: swap.id, approve: false })}
                      >
                        Ablehnen
                      </Button>
                    </>
                  )}
                </span>
              </motion.div>
            ))}
          </motion.div>
        )}
      </Card>

      <Card title="Meine Angebote">
        {mine.data?.items.length === 0 ? (
          <p className="muted">Du hast nichts angeboten.</p>
        ) : (
          <div className="stack stack--tight">
            {mine.data?.items.map((swap) => (
              <div key={swap.id} className="swap">
                <span>
                  <span className="swap__when">{when(swap)}</span>
                  <br />
                  <span className="muted">
                    {swap.claimed_by
                      ? `${swap.claimed_by} möchte übernehmen — wartet auf Freigabe`
                      : 'wartet auf jemanden'}
                  </span>
                </span>
                <Button variant="text" small onClick={() => withdraw.mutate(swap.id)}>
                  Zurückziehen
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Banner tone="info">
        Ein Tausch wird erst wirksam, wenn die Schichtleitung ihn freigibt. Bis dahin bleibt die
        Schicht bei der Person, die sie angeboten hat — verlasst euch also nicht darauf, bevor die
        Freigabe da ist.
      </Banner>
    </div>
  )
}

/** Nur importiert, damit der Konflikt-Farbton an einer Stelle definiert bleibt. */
export const conflictTone = SEVERITY_TONE

// ------------------------------------------------------------ Bewerbungen

/**
 * Offene Bewerbungen auf ausgeschriebene Schichten.
 *
 * Sitzt bei der Tauschbörse, weil beides dasselbe ist: etwas, das jemand aus
 * der Belegschaft anstößt und die Planung entscheidet.
 */
function PendingApplications() {
  const queryClient = useQueryClient()
  const toast = useToast()

  const offen = useQuery({
    queryKey: ['shift-applications'],
    queryFn: () => api.get<ListResponse<ShiftApplication>>('/api/shift-applications'),
  })

  const entscheiden = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      api.patch(`/api/shift-applications/${id}`, { accept }),
    onSuccess: (_daten, variablen) => {
      toast(variablen.accept ? 'Angenommen.' : 'Abgelehnt.', 'success')
      queryClient.invalidateQueries({ queryKey: ['shift-applications'] })
      queryClient.invalidateQueries({ queryKey: ['shifts'] })
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  const items = offen.data?.items ?? []
  if (offen.isPending || items.length === 0) return null

  return (
    <Card title={`Bewerbungen auf offene Schichten (${items.length})`}>
      <ul className="stack stack--tight" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((bewerbung) => (
          <li key={bewerbung.application_id} className="row row--between">
            <span>
              <strong>{bewerbung.full_name}</strong>
              <span className="muted">
                {' '}
                ·{' '}
                {new Date(bewerbung.starts_at).toLocaleString('de-DE', {
                  weekday: 'short',
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {' · '}
                {bewerbung.position_name} · {bewerbung.location_name}
              </span>
            </span>
            <span className="row">
              <Button
                small
                busy={entscheiden.isPending}
                onClick={() =>
                  entscheiden.mutate({ id: bewerbung.application_id, accept: true })
                }
              >
                Annehmen
              </Button>
              <Button
                variant="text"
                small
                onClick={() =>
                  entscheiden.mutate({ id: bewerbung.application_id, accept: false })
                }
              >
                Ablehnen
              </Button>
            </span>
          </li>
        ))}
      </ul>
      <p className="muted" style={{ fontSize: '0.8125rem', marginBottom: 0 }}>
        Beim Annehmen läuft dieselbe Prüfung wie bei jeder Einteilung von Hand — Ruhezeit,
        Höchstarbeitszeit, Doppelbuchung.
      </p>
    </Card>
  )
}
